import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/client";
import { OverviewStats } from "../types";

/* ── Animated counter ── */
function AnimatedNumber({ value, duration = 1200 }: { value: number; duration?: number }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    let start = 0;
    const step = Math.ceil(value / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= value) {
        start = value;
        clearInterval(timer);
      }
      setDisplay(start);
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);

  return <span ref={ref}>{display.toLocaleString("uz-UZ")}</span>;
}

/* ── Spinner ── */
function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="w-10 h-10 border-[3px] border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );
}

/* ── Stat card icons (inline SVG) ── */
const IconUniversity = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
);
const IconDirection = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
);
const IconUsers = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
);

export default function HomePage() {
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [subjects, setSubjects] = useState<string[]>([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats/overview").then((r) => {
      setStats(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));

    api.get("/filters/subjects", { params: { year: 2025 } }).then((r) => {
      setSubjects(r.data || []);
    }).catch(() => {});
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim()) {
      navigate(`/results?search=${encodeURIComponent(search.trim())}`);
    }
  };

  const handleSubjectSearch = () => {
    if (selectedSubject) {
      navigate(`/eligibility?subjects=${encodeURIComponent(selectedSubject)}`);
    }
  };

  return (
    <div>
      {/* ════════ Hero ════════ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-900 text-white">
        {/* Decorative blobs */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-indigo-500/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-indigo-400/20 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 text-sm font-medium text-indigo-100 mb-6 border border-white/10">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            2021 — 2025 yillar ma'lumotlari
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4 leading-tight">
            O'tish ballari — <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-amber-300 to-amber-400 bg-clip-text text-transparent">
              bitta joyda
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-indigo-200 max-w-2xl mx-auto mb-10 leading-relaxed">
            Barcha universitetlar va yo'nalishlar bo'yicha o'tish ballari, statistika va tahlillar
          </p>

          {/* Search */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-6">
            <div className="flex items-center bg-white rounded-2xl shadow-2xl shadow-indigo-900/30 overflow-hidden ring-1 ring-white/20">
              <div className="pl-5 text-slate-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Yo'nalish yoki universitet nomini qidiring..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 px-4 py-4 sm:py-4.5 text-slate-900 text-base sm:text-lg focus:outline-none bg-transparent placeholder:text-slate-400"
              />
              <button
                type="submit"
                className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white font-semibold px-6 sm:px-8 py-4 sm:py-4.5 transition-all m-1.5 rounded-xl"
              >
                Qidirish
              </button>
            </div>
          </form>

          <Link
            to="/eligibility"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-semibold text-base px-6 py-3 rounded-xl border border-white/20 transition-all hover:scale-[1.02]"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Ball yetadigan yo'nalishlar
          </Link>
        </div>
      </section>

      {/* ════════ Stats cards ════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
        {loading ? (
          <Spinner />
        ) : stats ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              { icon: <IconUniversity />, value: stats.total_universities, label: "Universitetlar", color: "indigo" },
              { icon: <IconDirection />, value: stats.total_directions, label: "Yo'nalishlar", color: "emerald" },
              { icon: <IconUsers />, value: stats.total_bot_users, label: "Bot foydalanuvchilari", color: "amber" },
            ].map((card) => (
              <div
                key={card.label}
                className="bg-white rounded-2xl shadow-sm shadow-slate-200/50 border border-slate-100 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    card.color === "indigo" ? "bg-indigo-50 text-indigo-600" :
                    card.color === "emerald" ? "bg-emerald-50 text-emerald-600" :
                    "bg-amber-50 text-amber-600"
                  }`}>
                    {card.icon}
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-900">
                      <AnimatedNumber value={card.value} />
                    </p>
                    <p className="text-sm text-slate-500 mt-0.5">{card.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      {/* ════════ Subject combination search ════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-6 sm:p-8 lg:p-10">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center flex-shrink-0 shadow-sm shadow-indigo-200">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                  Fanlar majmuasi bo'yicha qidirish
                </h2>
                <p className="text-slate-500 mt-1">
                  Fan majmuangizni tanlang va o'zingizga mos yo'nalishlarni toping
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="flex-1 border border-slate-200 rounded-xl px-4 py-3 text-slate-700 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:bg-white transition-colors"
              >
                <option value="">Fan majmuasini tanlang...</option>
                {subjects.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <button
                onClick={handleSubjectSearch}
                disabled={!selectedSubject}
                className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 disabled:from-slate-300 disabled:to-slate-300 text-white font-semibold px-8 py-3 rounded-xl transition-all shadow-sm shadow-indigo-200 disabled:shadow-none"
              >
                Qidirish
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ CTA - Ball yetadimi? ════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="relative overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-700 rounded-2xl p-8 sm:p-12">
          <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-white/5 rounded-full blur-2xl" />
          <div className="relative flex flex-col sm:flex-row items-center gap-6">
            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Balingiz yetadimi?
              </h2>
              <p className="text-emerald-100 text-lg max-w-lg">
                Balingizni kiriting — sizga mos barcha yo'nalishlarni ko'rsatamiz
              </p>
            </div>
            <Link
              to="/eligibility"
              className="inline-flex items-center gap-2 bg-white hover:bg-emerald-50 text-emerald-700 font-bold text-lg px-8 py-4 rounded-xl shadow-lg transition-all hover:scale-[1.02] flex-shrink-0"
            >
              Tekshirish
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ════════ Feature cards ════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[
            {
              to: "/eligibility",
              icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              ),
              color: "indigo",
              title: "Ball yetadimi?",
              desc: "Balingiz yetadigan barcha yo'nalishlarni ko'ring",
            },
            {
              to: "/universities",
              icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              ),
              color: "emerald",
              title: "Universitetlar",
              desc: "Barcha universitetlar va ularning yo'nalishlari",
            },
            {
              to: "/stats",
              icon: (
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              ),
              color: "amber",
              title: "Statistika",
              desc: "O'tish ballari tahlili va solishtirish",
            },
          ].map((card) => (
            <Link
              key={card.to}
              to={card.to}
              className="group bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-all hover:-translate-y-0.5"
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-colors ${
                card.color === "indigo"
                  ? "bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100"
                  : card.color === "emerald"
                  ? "bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100"
                  : "bg-amber-50 text-amber-600 group-hover:bg-amber-100"
              }`}>
                {card.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors mb-1">
                {card.title}
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                {card.desc}
              </p>
              <div className="mt-4 flex items-center text-indigo-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                Batafsil
                <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
